import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  DialogTitle,
} from '@tinct/web-components';
import { ImportResponse } from '@tinct/core-api-models';
import { CircleCheckIcon, InfoIcon } from 'lucide-react';
import { useStaticAssetsUrl } from '@tinct/core-utils';

export type SuccessStepProps = {
  importResult: ImportResponse;
  onDone: () => void;
};

export const SuccessStep = ({ importResult, onDone }: SuccessStepProps) => {
  const assetsUrl = useStaticAssetsUrl();

  const imported = importResult.successCount ?? 0;
  const errors = importResult.errorCount ?? 0;

  return (
    <div className="relative overflow-clip rounded-3xl border bg-background shadow-lg">
      {/* Cover image */}
      <div className="pt-2 px-2">
        <div className="overflow-clip rounded-2xl bg-indigo-200">
          <img
            src={`${assetsUrl}/import-done.png`}
            alt="Customers imported"
            className="w-full object-cover aspect-[2/1]"
          />
        </div>
      </div>

      {/* Head */}
      <div className="p-8 text-center">
        <DialogTitle className="text-2xl font-semibold tracking-[-0.4px]">
          Customers imported!
        </DialogTitle>
        <p className="text-base text-muted-foreground mt-3">
          Your logo pool just grew — Tinct will pick the most relevant logos for
          each visitor.
        </p>
      </div>

      {/* Alerts */}
      <div className="flex flex-col gap-2 px-8 pb-8">
        <div className="flex items-center gap-4 bg-teal-50 border border-teal-100 rounded-2xl p-4">
          <CircleCheckIcon className="size-4 shrink-0 text-teal-600" />
          <p className="text-sm font-medium text-teal-600 truncate">
            {imported} {imported === 1 ? 'customer' : 'customers'} added.
          </p>
        </div>

        {errors > 0 && (
          <Alert className="rounded-2xl">
            <InfoIcon className="size-4" />
            <AlertTitle>Info</AlertTitle>
            <AlertDescription>
              <ul className="list-disc ms-[21px]">
                <li>{errors} companies ignored : duplicates</li>
              </ul>
            </AlertDescription>
          </Alert>
        )}
      </div>

      {/* Footer */}
      <div className="px-8 pb-8">
        <Button onClick={onDone} size="lg" className="w-full">
          Done !
        </Button>
      </div>
    </div>
  );
};
